import { FeesRepo } from "@services/fees.repo";
import * as _ from "lodash";
import * as moment from "moment";
import * as echarts from "echarts";
import { exportToCSV } from "@common/utils/exportToCSV";
import { FeeCalculationType } from "../../fees";
import { extractErrorMessage } from "@common/utils/errorUtils";

class PortfolioFeesController implements angular.IOnInit {
  public feesByPortfolioLimit: number = 10;
  public topPortfolioFeeChart: any;
  public topPortfolioFeeChartOptions: any;
  public isLoadingPortfolioFees: boolean = false;
  public topPortfolioFees: Array<any>;
  public bottomPortfolioFees: Array<any>;
  private pendingRequests: number = 0;
  public portfolioFeeDate: Date;
  public bottomPortfolioFeeChart: any;
  public bottomPortfolioFeeChartOptions: any;
  public topPortfolioFeesGridOptions: any;
  public bottomPortfolioFeesGridOptions: any;
  public sortTopBottomPortfolioFeesByNav: boolean = false;
  public selectedFeeCalculationTypePortfolioFees: FeeCalculationType = FeeCalculationType.COMBINED;
  public showPortfolioFee: boolean = true;

  // Bindings from parent
  public lastMonth: Date;
  public selectedCurrency: string;
  public feeCalulationTypes: Array<any>;
  public exportPortfolioFeesRef: any;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private notifyjs: any,
    private $scope: angular.IScope
  ) {}

  private setLoading(loading: boolean) {
    if (loading) {
      this.pendingRequests++;
    } else {
      this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    }
    this.isLoadingPortfolioFees = this.pendingRequests > 0;
    if (this.exportPortfolioFeesRef) {
      this.exportPortfolioFeesRef.isLoading = this.isLoadingPortfolioFees;
    }
  }

  $onInit() {
    // Expose export method to parent
    if (this.exportPortfolioFeesRef) {
      this.exportPortfolioFeesRef.exportPortfolioFeesToCSV = this.exportPortfolioFeesToCSV;
    }

    this.topPortfolioFeeChart = echarts.init(
      document.getElementById("top-portfolio-fee-value-chart")
    );
    this.$scope.$watch(
      () =>
        this.topPortfolioFeeChart.getDom().parentElement.parentElement
          .clientHeight,
      this.topPortfolioFeeChart.resize()
    );

    this.bottomPortfolioFeeChart = echarts.init(
      document.getElementById("bottom-portfolio-fee-value-chart")
    );
    this.$scope.$watch(
      () =>
        this.bottomPortfolioFeeChart.getDom().parentElement.parentElement
          .clientHeight,
      this.bottomPortfolioFeeChart.resize()
    );

    this.topPortfolioFeesGridOptions = this.$scope[
      "topPortfolioFeesGridOptions"
    ] = {
      data: [],
      urlSync: false,
    };

    this.bottomPortfolioFeesGridOptions = this.$scope[
      "bottomPortfolioFeesGridOptions"
    ] = {
      data: [],
      urlSync: false,
    };

    // Watch for currency changes and update fees
    this.$scope.$watch(
      () => this.selectedCurrency,
      (newVal, oldVal) => {
        if (newVal !== oldVal && newVal) {
          this.updatePortfolioFees();
        }
      }
    );

    this.updatePortfolioFees();
  }

  updatePortfolioFees = () => {
    this.getTopPortfolioFeesByLimit();
    this.getBottomPortfolioFeesByLimit();
  };

  formatDate(selectedDate) {
    return moment(selectedDate).endOf("month").format("YYYY-MM");
  }

  getTopPortfolioFeesByLimit = () => {
    this.setLoading(true);
    this.feesRepo
      .getPortfolioFeesByLimit(
        this.portfolioFeeDate,
        this.feesByPortfolioLimit,
        true,
        this.selectedCurrency,
        this.selectedFeeCalculationTypePortfolioFees,
        this.sortTopBottomPortfolioFeesByNav
      )
      .then((res) => {
        this.topPortfolioFees = res.data as Array<any>;
        this.getTopPortfolioFeeGraph();
        this.topPortfolioFeesGridOptions.data = this.topPortfolioFees;
      })
      .catch((err) => {
        this.notifyjs("Failed to get top portfolio fees " + extractErrorMessage(err));
      })
      .finally(() => {
        this.setLoading(false);
      });
  };

  getBottomPortfolioFeesByLimit = () => {
    this.setLoading(true);
    this.feesRepo
      .getPortfolioFeesByLimit(
        this.portfolioFeeDate,
        this.feesByPortfolioLimit,
        false,
        this.selectedCurrency,
        this.selectedFeeCalculationTypePortfolioFees,
        this.sortTopBottomPortfolioFeesByNav
      )
      .then((res) => {
        this.bottomPortfolioFees = res.data as Array<any>;
        this.bottomPortfolioFeesGridOptions.data = this.bottomPortfolioFees;
        this.getBottomPortfolioFeeGraph();
      })
      .catch((err) => {
        this.notifyjs("Failed to get bottom portfolio fees " + extractErrorMessage(err));
      })
      .finally(() => {
        this.setLoading(false);
      });
  };

  getTopPortfolioFeeGraph = () => {
    let accountNumbers = this.topPortfolioFees.map(
      (portfolio) => portfolio.accountNumber
    );
    let feeValues;

    if (this.sortTopBottomPortfolioFeesByNav) {
      feeValues = this.topPortfolioFees.map((portfolio) =>
        portfolio.marketValue.toFixed(2)
      );
    } else {
      feeValues = this.topPortfolioFees.map((portfolio) =>
        portfolio.feeValue.toFixed(2)
      );
    }

    this.topPortfolioFeeChartOptions = {
      title: {
        text: `Top ${accountNumbers.length} Portfolio ${
          this.sortTopBottomPortfolioFeesByNav ? "NAVs" : "Fees"
        }`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          let feeParam = this.topPortfolioFees.filter(
            (feeParam) => feeParam.accountNumber == params[0].name
          )[0];
          return `Client : ${feeParam.clientName} <br>
                    Portfolio : ${feeParam.accountNumber} <br>
                    Fee Type : ${feeParam.feeTypeName} <br>
                    Fee Value : ${feeParam.feeValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} <br>
                    NAV : ${feeParam.marketValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} `;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
          dataZoom: {
            yAxisIndex: "none",
            title: {
              zoom: "Zoom In",
              back: "Back",
            },
          },
          restore: {
            title: "Restore",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: accountNumbers,
        barGap: 0,
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: `${this.sortTopBottomPortfolioFeesByNav ? "NAVs" : "Fees"} `,

          axisLabel: {
            formatter: (params) => {
              return params > 0
                ? `${this.selectedCurrency} ${this.normaliseNumbers(params)}`
                : "";
            },
          },
        },
      ],
      series: [
        {
          type: "bar",
          name: "Fee Value",
          data: feeValues,
        },
      ],
    };

    this.topPortfolioFeeChart.setOption(this.topPortfolioFeeChartOptions);
  };

  getBottomPortfolioFeeGraph = () => {
    let accountNumbers = this.bottomPortfolioFees
      .map((portfolio) => portfolio.accountNumber)
      .reverse();

    let feeValues;
    if (this.sortTopBottomPortfolioFeesByNav) {
      feeValues = this.bottomPortfolioFees
        .map((portfolio) => portfolio.marketValue.toFixed(2))
        .reverse();
    } else {
      feeValues = this.bottomPortfolioFees
        .map((portfolio) => portfolio.feeValue.toFixed(2))
        .reverse();
    }
    this.bottomPortfolioFeeChartOptions = {
      title: {
        text: `Bottom ${accountNumbers.length} Portfolio ${
          this.sortTopBottomPortfolioFeesByNav ? "NAVs" : "Fees"
        }`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          let feeParam = this.bottomPortfolioFees.filter(
            (feeParam) => feeParam.accountNumber == params[0].name
          )[0];
          return `Client : ${feeParam.clientName} <br>
                    Portfolio : ${feeParam.accountNumber} <br>
                    Fee Type : ${feeParam.feeTypeName} <br>
                    Fee Value : ${feeParam.feeValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} <br>
                    NAV : ${feeParam.marketValue.toLocaleString("en-EN", {
                      style: "currency",
                      currency: this.selectedCurrency,
                    })} `;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
          dataZoom: {
            yAxisIndex: "none",
            title: {
              zoom: "Zoom In",
              back: "Back",
            },
          },
          restore: {
            title: "Restore",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: accountNumbers,
        barGap: 0,
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: [
        {
          type: "value",
          name: `${this.sortTopBottomPortfolioFeesByNav ? "NAVs" : "Fees"} `,
          axisLabel: {
            formatter: (params) => {
              return params > 0
                ? `${this.selectedCurrency} ${this.normaliseNumbers(params)}`
                : "";
            },
          },
        },
      ],
      series: [
        {
          type: "bar",
          name: "Fee Value",
          data: feeValues,
        },
      ],
    };

    this.bottomPortfolioFeeChart.setOption(this.bottomPortfolioFeeChartOptions);
  };

  normaliseNumbers = (labelValue) => {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? Math.abs(Number(labelValue)) / 1.0e9 + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(labelValue)) >= 1.0e6
      ? Math.abs(Number(labelValue)) / 1.0e6 + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e3
      ? Math.abs(Number(labelValue)) / 1.0e3 + "K"
      : Math.abs(Number(labelValue));
  };

  exportPortfolioFeesToCSV = ($event: angular.IAngularEvent) => {
    $event.stopPropagation();
    exportToCSV(this.topPortfolioFees, "Top Portfolio Fees", true);
    exportToCSV(this.bottomPortfolioFees, "Bottom Portfolio Fees", true);
  };
}

export const portfolioFees: angular.IComponentOptions = {
  template: require("./portfolio-fees.html"),
  controller: PortfolioFeesController,
  bindings: {
    lastMonth: "<",
    selectedCurrency: "<",
    feeCalulationTypes: "<",
    portfolioFeeDate: "=",
    showPortfolioFee: "=",
    exportPortfolioFeesRef: "=",
  },
};




// WEBPACK FOOTER //
// ./src/app/fees/fees-components/portfolio-fees/portfolio-fees.ts